/**
 * Ticket Comment Thread Component
 * Displays comments with internal/external indicators and add comment form
 */

import { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Avatar,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material'
import { Lock, Send } from '@mui/icons-material'
import { TicketCommentDto, AddCommentRequest } from '@api/types/tickets'
import { safeFormatDistanceToNow } from '@utils/dateUtils'
import { useAuthStore } from '@store/authStore'

interface TicketCommentThreadProps {
  comments: TicketCommentDto[]
  onAddComment: (request: AddCommentRequest) => Promise<void>
  canAddInternalComments?: boolean
  isSubmitting?: boolean
}

export function TicketCommentThread({
  comments,
  onAddComment,
  canAddInternalComments = false,
  isSubmitting = false,
}: TicketCommentThreadProps) {
  const { user } = useAuthStore()
  const [newComment, setNewComment] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!newComment.trim()) {
      setError('Comment cannot be empty')
      return
    }

    try {
      await onAddComment({
        content: newComment.trim(),
        isInternal: canAddInternalComments ? isInternal : false,
      })

      setNewComment('')
      setIsInternal(false)
    } catch (err: any) {
      setError(err.message || 'Failed to add comment')
    }
  }

  const sortedComments = [...comments].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Comments ({comments.length})
      </Typography>

      {/* Comments List */}
      <Box sx={{ mb: 3 }}>
        {sortedComments.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
            <Typography variant="body2" color="text.secondary">
              No comments yet. Be the first to comment!
            </Typography>
          </Paper>
        ) : (
          sortedComments.map((comment, index) => (
            <Box key={comment.id}>
              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: comment.isInternal ? 'warning.light' : 'background.paper',
                  border: comment.isInternal ? '2px solid' : 'none',
                  borderColor: 'warning.main',
                }}
              >
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {(comment.authorDisplayName || 'U')[0].toUpperCase()}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {comment.authorDisplayName || 'Unknown User'}
                      </Typography>

                      {comment.isInternal && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: 'warning.dark',
                            fontSize: '0.75rem',
                          }}
                        >
                          <Lock fontSize="small" />
                          <Typography variant="caption" fontWeight="bold">
                            INTERNAL
                          </Typography>
                        </Box>
                      )}

                      <Typography variant="caption" color="text.secondary">
                        {safeFormatDistanceToNow(comment.createdAt)}
                      </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {comment.content}
                    </Typography>

                    {comment.attachments && comment.attachments.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Attachments: {comment.attachments.length}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Box>
          ))
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Add Comment Form */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Add Comment
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            placeholder="Write your comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={isSubmitting}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {canAddInternalComments && (
              <FormControlLabel
                control={
                  <Checkbox checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} size="small" />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Lock fontSize="small" />
                    <Typography variant="body2">Internal comment (moderators only)</Typography>
                  </Box>
                }
                disabled={isSubmitting}
              />
            )}

            <Button
              type="submit"
              variant="contained"
              endIcon={<Send />}
              disabled={isSubmitting || !newComment.trim()}
              sx={{ ml: 'auto' }}
            >
              {isSubmitting ? 'Posting...' : 'Post Comment'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
